import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ActCategory } from 'shell/models/act-category-list';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useEventCallback } from '@mui/material';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import RegistrationCitizenshipPage from 'mutation/pages/registration-citizenship/registration';
import { RegistrationCitizenshipFormVM } from 'mutation/view-models/registration-citizenship';
import { transformCitizenship } from 'notifications-from-civil-status/utils/transform-citizenship';

interface RegistrationCitizenshipNotificationSharedProps {
  goBackUrl?: string;
  goBackUrlDetail?: string;
  items?: BreadcrumbItem[];
  isNotDisabledCitizenship?: boolean;
}

const RegistrationCitizenshipNotificationShared = ({
  goBackUrl,
  goBackUrlDetail,
  items,
  isNotDisabledCitizenship,
}: RegistrationCitizenshipNotificationSharedProps): ReactElement => {
  const params = useParams();

  const idAnsc = useMemo(() => {
    return params.id || undefined;
  }, [params.id]);

  const idNotifica = params.idNotifica || '';

  const [dataEvent, getDataEvent] = useHttpFetch<
    HttpResponse<ActCategory>,
    RegistrationCitizenshipFormVM
  >(`${__ACT_API_URL__}/consultazione/evento`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformCitizenship,
  });

  useEffect(() => {
    if (idAnsc) {
      getDataEvent({
        data: {
          criteriRicercaEvento: {
            idAnsc,
          },
        },
      });
    }
  }, [idAnsc]);

  const saveData = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const navigate = useNavigate();

  const onConfirm = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.RegistrazioneEffettuata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrl || '');
    });
  });

  const onCancel = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveData({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.DaRegistrare,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate(goBackUrlDetail || '');
    });
  });

  return (
    <Fragment>
      <Breadcrumbs items={items || []} />
      {dataEvent.data && !dataEvent.pristine && !dataEvent.isLoading && !dataEvent.hasErrors ? (
        <RegistrationCitizenshipPage
          isWithoutBreadcrumbs
          initialValues={dataEvent.data as RegistrationCitizenshipFormVM}
          onConfirm={onConfirm}
          onCancel={onCancel}
          disabledFromAct
          isNotDisabledCitizenship={isNotDisabledCitizenship}
        />
      ) : undefined}
    </Fragment>
  );
};

RegistrationCitizenshipNotificationShared.displayName =
  'Registration Citizenship Notification Page component';

export default RegistrationCitizenshipNotificationShared;
